import { z } from 'zod'

/**
 * The code that identifies an account, and that a person sees at the front of every URL.
 *
 * Generated codes are `xxx-xxx` over lowercase letters and digits — short enough to read
 * aloud, long enough that 2.18 billion of them exist. The column behind it is plain text
 * rather than that shape, because an account may one day be given a chosen name such as
 * `frg` or `frg1dev`, and a chosen name has to behave exactly like a generated one
 * everywhere: same column, same lookups, same place in the URL.
 */

const CODE_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789'
const ID_GROUP_LENGTH = 3

/** What a generated account id looks like. A chosen name is looser; see `accountIdSchema`. */
export const GENERATED_ACCOUNT_ID_PATTERN = /^[a-z0-9]{3}-[a-z0-9]{3}$/

/**
 * What any account id may look like, generated or chosen. Loose enough for a future
 * chosen name, strict enough that a code is always safe in a path and never needs escaping.
 */
export const ACCOUNT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{1,29}$/

export const accountIdSchema = z
  .string()
  .regex(ACCOUNT_ID_PATTERN, 'An account id is lowercase letters, digits and hyphens.')

/**
 * Words that may never become an account id.
 *
 * Two different reasons, deliberately in one list because the consequence is the same. The
 * routing words sit in the very positions the router reads, so an account named `settings`
 * would make its own settings screen unreachable — a generated `xxx-xxx` can never collide
 * with them, but a chosen name could. The invite words and the archived words are reserved
 * for the same reason, in every language the product speaks, so that neither of those paths
 * is ever ambiguous.
 */
export const RESERVED_ACCOUNT_IDS: readonly string[] = [
  'settings',
  'messages',
  'operator',
  'threads',
  'chats',
  'sign-in',
  'api',
  'invite',
  'convidar',
  'invitar',
  'archived',
  'arquivadas',
  'archivadas',
]

/**
 * Sequences a generated code may not contain.
 *
 * Two billion random draws will eventually spell something nobody wants as their address.
 * The list is deliberately short: it covers what a three letter group can actually spell,
 * and it is meant to be added to by whoever first sees one it missed.
 */
const REFUSED_SEQUENCES: readonly string[] = [
  'ass',
  'cul',
  'cum',
  'fag',
  'fck',
  'fuc',
  'fuk',
  'kkk',
  'nig',
  'pau',
  'pns',
  'sex',
  'shi',
  'tit',
  'vag',
  'xxx',
]

/** Whether a generated candidate is fit to be offered to somebody as their address. */
export function isAcceptableAccountId(candidateAccountId: string): boolean {
  if (RESERVED_ACCOUNT_IDS.includes(candidateAccountId)) {
    return false
  }
  const codeWithoutSeparator = candidateAccountId.replace(/-/g, '')
  return !REFUSED_SEQUENCES.some(
    (refusedSequence) =>
      candidateAccountId.includes(refusedSequence) || codeWithoutSeparator.includes(refusedSequence),
  )
}

/**
 * One candidate code. Whether it is free is a question for the database, not for here.
 *
 * `randomDigits` is injected so a test can produce a known candidate; the caller in the
 * application always passes cryptographically random values.
 */
export function buildAccountIdFromRandomValues(randomValues: readonly number[]): string {
  const codeCharacters = randomValues
    .slice(0, ID_GROUP_LENGTH * 2)
    .map((randomValue) => CODE_ALPHABET[randomValue % CODE_ALPHABET.length] ?? 'a')
  return `${codeCharacters.slice(0, ID_GROUP_LENGTH).join('')}-${codeCharacters
    .slice(ID_GROUP_LENGTH)
    .join('')}`
}

/** How many random values `buildAccountIdFromRandomValues` needs. */
export const ACCOUNT_ID_RANDOM_VALUE_COUNT = ID_GROUP_LENGTH * 2
